import { fetchApi } from './UserApi';

type ChangePasswordErrorCode = 'invalid_credentials' | 'rate_limited' | 'not_authenticated' | 'invalid_password';

type ChangePasswordSuccessResponse = {
	success: true;
	redirect?: string;
};

type ChangePasswordErrorResponse = {
	error: true;
	code: ChangePasswordErrorCode;
};

export type ChangePasswordResponse = ChangePasswordSuccessResponse | ChangePasswordErrorResponse;

export type SaveDetailsPayload = {
	MarketClientABN: string;
	MarketClientFacebookId: string;
	MarketClientInstagramId: string;
	MarketClientWebsiteUrl: string;
	ContactPrimaryContactNumber: string;
	ClientStreetAddress1: string;
	ClientStreetAddress2: string;
	ClientAddressCity: string;
	ClientAddressState: string;
	ClientAddressPostcode: string;
};

export type AccountDetails = {
	stallName: string;
	abn: string;
	facebook: string;
	instagram: string;
	website: string;
	contactEmail: string;
	contactNumber: string;
	streetAddress1: string;
	streetAddress2: string;
	suburb: string;
	state: string;
	postcode: string;
};

type PasswordRequirement = {
	message: string;
	isValid: (password: string) => boolean;
};

type PasswordValidation =
	| {
			valid: true;
	  }
	| {
			valid: false;
			failures: string[];
	  };

export default class AccountService {
	private static passwordRequirements: PasswordRequirement[] = [
		{
			message: 'Password must be at least 8 characters long.',
			isValid: (password) => password.length >= 8,
		},
		{
			message: 'Password must be 50 characters or fewer.',
			isValid: (password) => password.length <= 50,
		},
		{
			message: 'Password must include at least one lowercase letter.',
			isValid: (password) => /[a-z]/.test(password),
		},
		{
			message: 'Password must include at least one uppercase letter.',
			isValid: (password) => /[A-Z]/.test(password),
		},
		{
			message: 'Password must include at least one number.',
			isValid: (password) => /\d/.test(password),
		},
	];

	public static changePassword(hash: string, currentPassword: string, newPassword: string) {
		return fetchApi<ChangePasswordResponse>({
			route: 'account/api/change-password',
			data: {
				hash,
				current_password: currentPassword,
				new_password: newPassword,
			},
			errorMessage: 'Unable to change password.',
		}).then((res) => res.data);
	}

	public static getDetails(hash: string) {
		return fetchApi<{ details: AccountDetails }>({
			route: 'account/api/details',
			data: { hash },
			errorMessage: 'Error loading your details',
		}).then((res) => res.data.details);
	}

	public static saveDetails(hash: string, payload: SaveDetailsPayload) {
		return fetchApi<{ details: AccountDetails }>({
			route: 'account/api/save-details',
			data: {
				...payload,
				hash,
			},
			errorMessage: 'Error saving your details',
		}).then((res) => res.data.details);
	}

	public static getPasswordRequirementMessage() {
		return 'Password must be at least 8 characters and contain a lowercase letter, uppercase letter, and a number.';
	}

	public static validatePassword(password: string): PasswordValidation {
		const failures = AccountService.passwordRequirements
			.filter((requirement) => !requirement.isValid(password))
			.map((requirement) => requirement.message);
		if (failures.length > 0) {
			return { valid: false, failures };
		}
		return { valid: true };
	}
}
